import { useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { field, card } from "@/components/admin/SettingsPanel";

export const SchedulePanel = () => {
  const { settings, setSettings } = useSite();
  const [rows, setRows] = useState(() => (settings.schedule || []).map((r) => ({ ...r })));
  const [busy, setBusy] = useState(false);
  const upd = (i, k) => (e) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const schedule = rows.filter((r) => r.time && r.en).sort((a, b) => a.time.localeCompare(b.time));
      const { data } = await api.put("/settings", { schedule });
      setSettings((p) => ({ ...p, ...data }));
      setRows(data.schedule);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="schedule-panel" className={card}>
      <h3 className="font-display text-2xl text-[#F4EEE2]">Daily broadcast schedule</h3>
      <p className="mt-1 text-sm text-[#B7BFCC]">Shown on the Home page. Rows are sorted by time when saved.</p>
      <div className="mt-6 grid gap-3">
        {rows.map((r, i) => (
          <div key={i} data-testid={`schedule-row-${i}`} className="grid gap-2 rounded-xl border border-[#F4EEE2]/10 p-3 sm:grid-cols-[120px_1fr_1fr_auto] sm:border-0 sm:p-0">
            <input data-testid={`schedule-time-${i}`} type="time" value={r.time} onChange={upd(i, "time")} className={field} />
            <input data-testid={`schedule-en-${i}`} placeholder="Program (English)" value={r.en} onChange={upd(i, "en")} className={field} />
            <input data-testid={`schedule-mh-${i}`} placeholder="Program (Marshallese)" value={r.mh} onChange={upd(i, "mh")} className={field} />
            <button data-testid={`schedule-remove-${i}`} onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="flex w-fit items-center gap-1.5 rounded-full p-2 text-xs text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Remove"><Trash2 className="h-4 w-4" /><span className="sm:hidden">Remove</span></button>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="schedule-add-btn" onClick={() => setRows((p) => [...p, { time: "", en: "", mh: "" }])} className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 font-ui text-sm font-semibold text-[#F4EEE2]">
          <Plus className="h-4 w-4" /> Add program
        </button>
        <button data-testid="schedule-save-btn" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save schedule
        </button>
      </div>
    </div>
  );
};
